import { CellComponentProps } from '@/components/collapsible-table';
import { EMPTY_FIELD_PLACEHOLDER } from '@/consts/formatting';
import formatters from '@/utils/Formatters';
import {
  CheckCircle,
  FlagOutlined,
  PendingOutlined,
  WarningOutlined
} from '@mui/icons-material';
import { Box, Stack, TableCell, Typography } from '@mui/material';
import { grey, orange } from '@mui/material/colors';

import clsx from 'clsx';
import dayjs from 'dayjs';

import { useStyles } from './PlanTableCell.component';

const previousStatusColumnPerColumn: Record<string, string> = {
  CLIENT_SIGNED_PAA: 'DIGITAL_ONBOARDING_STATE_PLAN_LAUNCH_CALL',
  DIGITAL_ONBOARDING_STATE_PLAN_LAUNCH_CALL: 'PAYROLL_SETUP',
  PAYROLL_SETUP: 'PSA_SIGNED_DATE',
  PSA_SIGNED_DATE: 'QUESTIONNAIRE_SUBMITTED_AT'
};

const getDateFromTracker = (
  row: any,
  column: string
): { date: string | null; isCompleted: boolean } => {
  switch (column) {
    case 'QUESTIONNAIRE_SUBMITTED_AT': {
      const date = row?.pizzaTracker?.QUESTIONNAIRE_SUBMITTED_AT;
      return { date, isCompleted: !!date };
    }
    case 'PSA_SIGNED_DATE': {
      const date = row?.pizzaTracker?.PSA_SIGNED_DATE;
      return { date, isCompleted: !!date };
    }
    case 'PAYROLL_SETUP': {
      const taskTracker = row?.pizzaTracker?.statefulSchemaTracking;
      const date = taskTracker?.updatedAt || taskTracker?.createdAt;
      const isCompleted = taskTracker?.status === 'completed_successfully';
      return { date, isCompleted };
    }
    case 'DIGITAL_ONBOARDING_STATE_PLAN_LAUNCH_CALL': {
      const date = row?.pizzaTracker?.DIGITAL_ONBOARDING_STATE_PLAN_LAUNCH_CALL;
      return { date, isCompleted: !!date };
    }
    case 'CLIENT_SIGNED_PAA': {
      const date = row?.pizzaTracker?.CLIENT_SIGNED_PAA;
      return { date, isCompleted: !!date };
    }
    case 'EMPLOYEE_INVITES': {
      const sent = row?.pizzaTracker?.EMPLOYEE_INVITES_SENT;
      const scheduled = row?.pizzaTracker?.EMPLOYEE_INVITES_SCHEDULED;
      return {
        date: sent || scheduled,
        isCompleted: !!sent
      };
    }
    case 'FIRST_PAYROLL': {
      const complete = row?.pizzaTracker?.PAYROLL_COMPLETE;
      const submitted = row?.pizzaTracker?.PAYROLL_SUBMITTED;
      const scheduled = row?.pizzaTracker?.PAYROLL_SCHEDULED;
      return {
        date: complete || submitted || scheduled,
        isCompleted: !!complete
      };
    }
    default:
      return { date: null, isCompleted: false };
  }
};

export const checkPreviousStatusColumnOverdue = (
  row: any,
  columnName: string
): boolean => {
  const previousColumn = previousStatusColumnPerColumn[columnName];
  if (!previousColumn) return false;

  const { date: prevDate, isCompleted: prevCompleted } = getDateFromTracker(
    row,
    previousColumn
  );
  const { isCompleted: currentCompleted } = getDateFromTracker(row, columnName);

  if (!prevCompleted || currentCompleted || !prevDate) return false;

  return dayjs().isAfter(dayjs(prevDate).add(7, 'day'));
};

export const TrackerStatusCell: React.FunctionComponent<CellComponentProps> = ({
  row,
  column
}) => {
  let isOverdue = false;
  let status = 'Pending';
  let date: string | null = null;

  const disableOverdue = ['FIRST_PAYROLL', 'EMPLOYEE_INVITES'].includes(
    column.field
  );

  const classes = useStyles();
  const cssClasses = clsx(classes.size, column.cellClassName);

  switch (column.field) {
    case 'CONVERSION_WIRE_RECEIVED':
      date = row?.pizzaTracker?.CONVERSION_WIRE_RECEIVED;
      status = date ? 'Complete' : 'Pending';
      break;
    case 'QUESTIONNAIRE_SUBMITTED_AT':
      date = row?.pizzaTracker?.QUESTIONNAIRE_SUBMITTED_AT;
      status = date ? 'Complete' : 'Pending';
      break;

    case 'PSA_SIGNED_DATE':
      date = row?.pizzaTracker?.PSA_SIGNED_DATE;
      status = date ? 'Signed' : 'Pending';
      break;

    case 'PAYROLL_SETUP': {
      const taskTracker = row?.pizzaTracker?.statefulSchemaTracking;
      date = taskTracker?.updatedAt || taskTracker?.createdAt;
      status =
        taskTracker?.status === 'completed_successfully'
          ? 'Complete'
          : !taskTracker?.status
            ? 'Not Started'
            : 'In Progress';
      break;
    }

    case 'DIGITAL_ONBOARDING_STATE_PLAN_LAUNCH_CALL':
      date = row?.pizzaTracker?.DIGITAL_ONBOARDING_STATE_PLAN_LAUNCH_CALL;
      status = date ? 'Complete' : 'Pending';
      break;

    case 'CLIENT_SIGNED_PAA':
      date = row?.pizzaTracker?.CLIENT_SIGNED_PAA;
      status = date ? 'Signed' : 'Pending';
      break;

    case 'EMPLOYEE_INVITES':
      date =
        row?.pizzaTracker?.EMPLOYEE_INVITES_SENT ||
        row?.pizzaTracker?.EMPLOYEE_INVITES_SCHEDULED;
      if (row?.pizzaTracker?.EMPLOYEE_INVITES_SENT) status = 'Sent';
      else if (row?.pizzaTracker?.EMPLOYEE_INVITES_SCHEDULED)
        status = 'Scheduled';
      break;

    case 'FIRST_PAYROLL':
      date =
        row?.pizzaTracker?.PAYROLL_COMPLETE ||
        row?.pizzaTracker?.PAYROLL_SUBMITTED ||
        row?.pizzaTracker?.PAYROLL_SCHEDULED;
      if (row?.pizzaTracker?.PAYROLL_COMPLETE) status = 'Complete';
      else if (row?.pizzaTracker?.PAYROLL_SUBMITTED) status = 'Submitted';
      else if (row?.pizzaTracker?.PAYROLL_SCHEDULED) status = 'Scheduled';
      break;
  }

  const dateFormatted = date
    ? formatters.formatFromIsoDateCustom(date, 'MM/DD/YYYY')
    : EMPTY_FIELD_PLACEHOLDER;

  isOverdue = checkPreviousStatusColumnOverdue(row, column.field);

  const backgroundColor =
    isOverdue && !disableOverdue ? orange[50] : 'transparent';

  return (
    <TableCell
      className={cssClasses}
      component='th'
      scope='row'
      sx={{ backgroundColor }}>
      <Box>
        <Stack alignItems='center' flexDirection='row'>
          <Stack alignItems='center' flexDirection='row' mr={1}>
            {dateFormatted === EMPTY_FIELD_PLACEHOLDER ? null : status ===
              'In Progress' ? (
              <PendingOutlined color='primary' fontSize='small' />
            ) : status === 'Scheduled' ? (
              <FlagOutlined fontSize='small' sx={{ color: grey[500] }} />
            ) : (
              <CheckCircle color='success' fontSize='small' />
            )}
          </Stack>

          <Stack flexDirection='column'>
            {status !== 'Pending' && <Typography>{status}</Typography>}
            <Stack alignItems='center' flexDirection='row'>
              <Typography color='text.secondary' mr={0.5}>
                {dateFormatted}
              </Typography>
              {isOverdue && !disableOverdue && (
                <WarningOutlined color='warning' fontSize='small' />
              )}
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </TableCell>
  );
};
